/**
 * Sales Order to Work Order Dialog
 * Converts a Sales Order into a Work Order (supports multi-select and grouping)
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Space,
  Alert,
  Divider,
  Typography,
  Row,
  Col,
  Card,
  Tag,
  Radio,
  Collapse,
  Button,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined, ToolOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useWarehouses, useItems, useItemsWithStock } from '@/hooks/useInventory';
import type { Item } from '@/services/inventory.service';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import { useUsers } from '@/hooks/useUsers';
import { useAuthStore } from '@/store/authStore';
import type {
  ConversionData,
  MaterialUtilizationLineInput,
  WorkOrderItem,
} from '@/services/workOrder.service';
import {
  WORK_ORDER_DEVIATION_TYPES,
  WORK_ORDER_EXECUTION_SCOPES,
} from '@/services/workOrder.service';

const { Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

type MaterialFormRow = {
  drawingLineNo?: string;
  /** Selected inventory item (UI); client spec string is derived on submit. */
  materialUtilItemId?: string;
  clientMaterialSpecification?: string;
  materialGrade?: string;
  headNo?: string;
  verification?: 'verified' | 'not_verified';
  remarks?: string;
};

/** Code, name, and description for mat. spec. dropdown and persisted client spec (dedupe consecutive repeats). */
function buildItemMatSpecDisplay(it: Item): string {
  const parts = [it.item_code, it.item_name, it.item_description]
    .map((s) => String(s ?? '').trim())
    .filter(Boolean)
    .filter((s, i, arr) => i === 0 || s !== arr[i - 1]);
  return parts.join(' — ') || it.item_name || it.item_code || it.id;
}

function normalizeMaterialRows(
  raw: MaterialFormRow[] | undefined,
  inventoryItems: Item[]
): MaterialUtilizationLineInput[] {
  if (!raw?.length) return [];
  const byId = new Map(inventoryItems.map((i) => [i.id, i]));
  return raw.map((r) => {
    const itemId = (r.materialUtilItemId ?? '').trim();
    const it = itemId ? byId.get(itemId) : undefined;
    const specFromItem = it ? buildItemMatSpecDisplay(it) : '';
    const spec = (specFromItem || (r.clientMaterialSpecification ?? '')).trim();
    return {
      drawingLineNo: (r.drawingLineNo ?? '').trim(),
      clientMaterialSpecification: spec || undefined,
      materialGrade: (r.materialGrade ?? '').trim() || undefined,
      headNo: (r.headNo ?? '').trim() || undefined,
      verified: r.verification === 'verified',
      notVerified: r.verification === 'not_verified',
      remarks: (r.remarks ?? '').trim() || undefined,
    };
  });
}

function validateMaterialFormRows(rows: MaterialFormRow[]): string | null {
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const t = (s?: string) => (s ?? '').trim();
    const hasContent =
      t(r.drawingLineNo) !== '' ||
      t(r.materialUtilItemId) !== '' ||
      t(r.clientMaterialSpecification) !== '' ||
      t(r.materialGrade) !== '' ||
      t(r.headNo) !== '' ||
      t(r.remarks) !== '';
    if (!hasContent) continue;
    if (t(r.drawingLineNo) === '') {
      return `Material utilization row ${i + 1}: Drawing line no. is required when the row has data.`;
    }
    if (r.verification !== 'verified' && r.verification !== 'not_verified') {
      return `Material utilization row ${i + 1}: Select Verified or Not verified.`;
    }
  }
  return null;
}

type MaterialStockInfo = {
  available: number;
  reserved: number;
  reorderLevel: number;
  minimumLevel: number;
  uom: string;
};

function buildMaterialStockByItemId(rows: unknown[]): Map<string, MaterialStockInfo> {
  const m = new Map<string, MaterialStockInfo>();
  for (const raw of rows) {
    const row = raw as Record<string, unknown>;
    const idVal = row.item_id ?? row.id;
    const id = idVal != null && String(idVal).trim() !== '' ? String(idVal) : '';
    if (!id) continue;
    const reorder = Number(row.item_reorder_level ?? row.reorder_level ?? 0);
    const minimum = Number(row.item_minimum_stock_level ?? row.minimum_stock_level ?? 0);
    m.set(id, {
      available: Number(row.available_qty ?? row.current_stock ?? 0) || 0,
      reserved: Number(row.reserved_qty ?? 0) || 0,
      reorderLevel: Number.isFinite(reorder) ? reorder : 0,
      minimumLevel: Number.isFinite(minimum) ? minimum : 0,
      uom: String(
        row.item_uom ?? row.item_base_uom ?? row.uom ?? row.base_uom ?? 'PCS'
      ).trim() || 'PCS',
    });
  }
  return m;
}

function MaterialUtilStockHint({
  itemId,
  stockByItemId,
  loading,
  scopeLabel,
}: {
  itemId?: string;
  stockByItemId: Map<string, MaterialStockInfo>;
  loading: boolean;
  scopeLabel: string;
}) {
  if (!itemId?.trim()) return null;
  if (loading) {
    return (
      <div style={{ marginTop: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Loading stock…
        </Text>
      </div>
    );
  }
  const s = stockByItemId.get(itemId);
  if (!s) {
    return (
      <div style={{ marginTop: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          No stock summary for this item (it may not be stock-tracked).
        </Text>
      </div>
    );
  }
  const critical =
    s.available <= 0 || (s.minimumLevel > 0 && s.available <= s.minimumLevel);
  const low = !critical && s.reorderLevel > 0 && s.available <= s.reorderLevel;
  const statusTag = critical ? (
    <Tag color="red">Below minimum / out of stock</Tag>
  ) : low ? (
    <Tag color="orange">At or below reorder level</Tag>
  ) : (
    <Tag color="green">Stock OK</Tag>
  );
  return (
    <div style={{ marginTop: 4 }}>
      <Space size={8} wrap align="center">
        <Text type="secondary" style={{ fontSize: 12 }}>
          {scopeLabel}: Available <Text strong>{s.available}</Text> {s.uom}
          {s.reserved > 0 ? (
            <>
              {' '}
              · Reserved <Text strong>{s.reserved}</Text>
            </>
          ) : null}
        </Text>
        {statusTag}
      </Space>
    </div>
  );
}

type MatSpecItemSelectProps = {
  restField: Omit<React.ComponentProps<typeof Form.Item>, 'name' | 'children'>;
  name: number;
  prefixPath: (string | number)[];
  inventoryItems: Item[];
  loading: boolean;
  stockByItemId: Map<string, MaterialStockInfo>;
  stockLoading: boolean;
  stockScopeLabel: string;
};

/** Searchable item picker; fills material grade and heat no. from item profile (heat no. remains editable). */
const MatSpecItemSelect: React.FC<MatSpecItemSelectProps> = ({
  restField,
  name,
  prefixPath,
  inventoryItems,
  loading,
  stockByItemId,
  stockLoading,
  stockScopeLabel,
}) => {
  const form = Form.useFormInstance();
  const watchedItemId = Form.useWatch([...prefixPath, name, 'materialUtilItemId'], form) as string | undefined;
  const stockQtyByItemId = useMemo(() => {
    const m = new Map<string, number>();
    stockByItemId.forEach((info, id) => {
      const free = Math.max(0, Number(info.available) - Number(info.reserved || 0));
      m.set(id, free);
    });
    return m;
  }, [stockByItemId]);

  const applyProfile = (itemId?: string | null) => {
    const base = [...prefixPath, name];
    if (!itemId) {
      form.setFields([
        { name: [...base, 'materialGrade'], value: '' },
        { name: [...base, 'headNo'], value: '' },
      ]);
      return;
    }
    const it = inventoryItems.find((i) => i.id === itemId);
    if (!it) return;
    form.setFields([
      { name: [...base, 'materialGrade'], value: it.profile_grade ?? '' },
      { name: [...base, 'headNo'], value: it.profile_heat_no ?? '' },
    ]);
  };

  return (
    <div>
      <Form.Item
        {...restField}
        label="Client mat. spec."
        name={[name, 'materialUtilItemId']}
        tooltip="Choose from inventory items. Grade and head no. load from the item profile (head no. from heat no.); you can edit head no. below."
      >
        <ItemSelect
          items={inventoryItems}
          autoLoad={false}
          loading={loading}
          placeholder="Search items"
          showStock
          stockByItemId={stockQtyByItemId}
          popupMatchSelectWidth={false}
          dropdownStyle={{
            minWidth: 560,
            maxWidth: 'min(720px, calc(100vw - 32px))',
          }}
          popupClassName="mat-spec-item-select-dropdown"
          onChange={(v) => applyProfile(v != null ? String(v) : null)}
        />
      </Form.Item>
      <MaterialUtilStockHint
        itemId={watchedItemId}
        stockByItemId={stockByItemId}
        loading={stockLoading}
        scopeLabel={stockScopeLabel}
      />
    </div>
  );
};

export type SalesOrderToWorkOrderLayout = 'modal' | 'page';

interface SalesOrderToWorkOrderDialogProps {
  /** 'modal' (default): Ant Design Modal. 'page': full-page body only (no Modal). */
  variant?: SalesOrderToWorkOrderLayout;
  /** Sales order flow (default) vs production request (labels and copy). */
  documentKind?: 'sales-order' | 'production-request';
  /** Pre-fill target warehouse (e.g. production request default). */
  defaultTargetWarehouseId?: string;
  visible?: boolean;
  open?: boolean;
  onClose: () => void;
  salesOrderId: string;
  salesOrderNumber: string;
  items: Array<{
    id: string;
    item_code?: string;
    item_name?: string;
    itemName?: string;
    item_description?: string;
    quantity?: number;
    ordered_quantity?: number;
    uom?: string;
    delivery_date?: string;
    deliveryDate?: string;
    required_date?: string;
  }>;
  onSuccess?: () => void;
  onSubmit?: (data: ConversionData) => void | Promise<void>;
}

export const SalesOrderToWorkOrderDialog: React.FC<SalesOrderToWorkOrderDialogProps> = ({
  variant = 'modal',
  documentKind = 'sales-order',
  defaultTargetWarehouseId,
  visible,
  open,
  onClose,
  salesOrderId,
  salesOrderNumber,
  items,
  onSuccess,
  onSubmit,
}) => {
  const isPr = documentKind === 'production-request';
  const isPage = variant === 'page';
  const isOpen = isPage || (open ?? visible ?? false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>({});
  const { data: warehouses = [] } = useWarehouses();
  const { data: inventoryItems = [], isLoading: inventoryItemsLoading } = useItems({ is_active: true });
  const targetWarehouseId = Form.useWatch('targetWarehouseId', form) as string | undefined;
  const { data: stockRows = [], isLoading: stockLoading } = useItemsWithStock(
    targetWarehouseId?.trim() ? targetWarehouseId : undefined,
    { enabled: isOpen }
  );
  const materialStockByItemId = useMemo(() => buildMaterialStockByItemId(stockRows as unknown[]), [stockRows]);
  const materialStockScopeLabel = useMemo(() => {
    const wid = targetWarehouseId?.trim();
    if (wid) {
      const wh = warehouses.find((w) => w.id === wid);
      return wh ? `Stock at ${wh.warehouse_name}` : 'Stock (target warehouse)';
    }
    return 'Stock (all warehouses)';
  }, [targetWarehouseId, warehouses]);
  const { user } = useAuthStore();
  const { data: users = [], isLoading: usersLoading } = useUsers({
    company_id: user?.company_id,
    is_active: true,
  });

  // Normalize items: API returns item_description, ordered_quantity, item_code, uom
  const normalizedItems = items.map((item) => ({
    id: item.id,
    itemCode: item.item_code || '',
    itemName:
      item.item_name ||
      item.itemName ||
      item.item_description ||
      item.item_code ||
      'Item',
    quantity: Number(item.quantity ?? item.ordered_quantity ?? 0),
    uom: item.uom || 'PCS',
    deliveryDate: item.delivery_date || item.deliveryDate || item.required_date,
  }));

  const selectedItems = normalizedItems.filter((item) => selectedItemIds.includes(item.id));

  // When selection changes, init quantities to ordered qty
  useEffect(() => {
    const next: Record<string, number> = {};
    selectedItemIds.forEach((id) => {
      const item = normalizedItems.find((i) => i.id === id);
      next[id] = itemQuantities[id] ?? item?.quantity ?? 1;
    });
    setItemQuantities((prev) => ({ ...prev, ...next }));
  }, [selectedItemIds.join(',')]);

  const handleSubmit = async () => {
    if (submitting) return;
    try {
      const values = await form.validateFields();
      const targetWarehouseId = values.targetWarehouseId;
      const plannedStartDate = values.plannedStartDate?.format?.('YYYY-MM-DD') ?? values.plannedStartDate;
      const plannedEndDate = values.plannedEndDate?.format?.('YYYY-MM-DD') ?? values.plannedEndDate;
      const priority = values.priority || 'Normal';
      const notes = values.notes;
      const oneWOForAll = values.grouping === 'one';

      if (!targetWarehouseId) {
        form.setFields([{ name: 'targetWarehouseId', errors: ['Please select target warehouse'] }]);
        return;
      }
      if (selectedItems.length === 0) {
        form.setFields([{ name: 'salesOrderItemIds', errors: ['Please select at least one item'] }]);
        return;
      }

      const sharedRows = (values.sharedMaterials || []) as MaterialFormRow[];
      const lineMaterials = (values.lineMaterials || {}) as Record<string, MaterialFormRow[]>;

      const sharedErr = validateMaterialFormRows(sharedRows);
      if (sharedErr) {
        Modal.error({ title: 'Material utilization', content: sharedErr });
        return;
      }
      if (!oneWOForAll) {
        for (const it of selectedItems) {
          const rows = lineMaterials[it.id] || [];
          const err = validateMaterialFormRows(rows);
          if (err) {
            Modal.error({ title: `Material utilization — ${it.itemName}`, content: err });
            return;
          }
        }
      }

      const workOrderItems: WorkOrderItem[] = selectedItems.map((it) => ({
        lineItemId: it.id,
        quantity: itemQuantities[it.id] ?? it.quantity,
        itemCode: it.itemCode,
        itemDescription: it.itemName,
        uom: it.uom,
      }));

      const commonFields = {
        targetWarehouseId,
        plannedStartDate,
        plannedEndDate,
        priority,
        notes,
        executionScope: values.executionScope,
        deviationType: values.deviationType,
        clientScopeOfWork: values.clientScopeOfWork?.trim() || null,
        clientQualityRequirements: values.clientQualityRequirements?.trim() || null,
        productionSupervisorUserId: values.productionSupervisorUserId,
        qualityEngineerUserId: values.qualityEngineerUserId,
      };

      let groups: ConversionData['groups'];
      if (oneWOForAll) {
        groups = [
          {
            ...commonFields,
            items: workOrderItems,
            materialUtilizationLines: normalizeMaterialRows(sharedRows, inventoryItems),
          },
        ];
      } else {
        groups = workOrderItems.map((it) => ({
          ...commonFields,
          items: [it],
          materialUtilizationLines: normalizeMaterialRows(lineMaterials[it.lineItemId] || [], inventoryItems),
        }));
      }

      setSubmitting(true);
      try {
        if (onSubmit) {
          await onSubmit({ groups });
        }
        form.resetFields();
        setSelectedItemIds([]);
        setItemQuantities({});
        onSuccess?.();
        // Modal: close after success. Full page: onSuccess usually navigates away—do not call onClose or it can override navigation.
        if (!isPage) {
          onClose();
        }
      } finally {
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedItemIds([]);
    setItemQuantities({});
    onClose();
  };

  const userOptions = users.map((u) => ({
    value: u.id,
    label: `${u.first_name} ${u.last_name || ''} (${u.email})`.trim(),
  }));

  const titleBlock = (
    <Space>
      <ToolOutlined style={{ color: '#722ed1' }} />
      <Text strong>
        {isPr ? 'Create Work Order from Production Request' : 'Create Work Order from Customer Order'}
      </Text>
    </Space>
  );

  const body = (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          message={isPr ? 'Convert Production Request to Production' : 'Convert Customer Order to Production'}
          description="Select items, warehouse, and dates. Capture execution scope, client requirements, material utilization, and assigned leads for production and quality."
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />

        <Card size="small" style={{ background: '#fafafa' }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={24}>
                <Text type="secondary">{isPr ? 'Production request:' : 'Customer Order:'}</Text>
                <br />
                <Text strong>{salesOrderNumber}</Text>
              </Col>
            </Row>
          </Space>
        </Card>

        <Divider style={{ margin: '12px 0' }} />

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            grouping: 'one',
            priority: 'Normal',
            plannedStartDate: dayjs(),
            plannedEndDate: dayjs().add(7, 'days'),
            lineMaterials: {},
            ...(defaultTargetWarehouseId ? { targetWarehouseId: defaultTargetWarehouseId } : {}),
          }}
        >
          <Collapse defaultActiveKey={['basics', 'execution', 'client', 'responsibility', 'material']}>
            <Panel header="Work order basics" key="basics">
              <Form.Item
                label="Select Items to Manufacture"
                name="salesOrderItemIds"
                rules={[{ required: true, message: 'Please select at least one item', type: 'array', min: 1 }]}
              >
                <Select
                  mode="multiple"
                  placeholder={isPr ? 'Choose lines from production request' : 'Choose items from customer order'}
                  value={selectedItemIds}
                  onChange={(value: string[]) => {
                    setSelectedItemIds(value);
                    form.setFieldsValue({ salesOrderItemIds: value });
                  }}
                  optionLabelProp="label"
                >
                  {normalizedItems.map((item) => (
                    <Select.Option key={item.id} value={item.id} label={item.itemName}>
                      <Space>
                        <Text>{item.itemName}</Text>
                        <Tag color="blue">Qty: {item.quantity}</Tag>
                        {item.deliveryDate && (
                          <Tag color="orange">
                            Due: {dayjs(item.deliveryDate).format('MMM DD, YYYY')}
                          </Tag>
                        )}
                      </Space>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {selectedItems.length > 0 && (
                <Form.Item label="Quantity per item">
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {selectedItems.map((item) => (
                      <Row key={item.id} gutter={8} align="middle">
                        <Col flex="1">
                          <Text type="secondary">{item.itemName}</Text>
                          <Tag style={{ marginLeft: 8 }}>
                            {isPr ? 'Remaining' : 'Ordered'}: {item.quantity}
                          </Tag>
                        </Col>
                        <Col>
                          <InputNumber
                            min={1}
                            value={itemQuantities[item.id]}
                            onChange={(val) =>
                              setItemQuantities((prev) => ({ ...prev, [item.id]: Number(val) || 1 }))
                            }
                            style={{ width: 100 }}
                          />
                        </Col>
                      </Row>
                    ))}
                  </Space>
                </Form.Item>
              )}

              <Form.Item label="Create work order(s)" name="grouping" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value="one">One work order for all selected items</Radio>
                  <Radio value="each">One work order per item</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label="Target Warehouse"
                name="targetWarehouseId"
                rules={[{ required: true, message: 'Please select target warehouse' }]}
              >
                <Select
                  placeholder="Select warehouse"
                  showSearch
                  optionFilterProp="label"
                  options={warehouses.map((w: { id: string; warehouse_name: string; warehouse_code?: string }) => ({
                    value: w.id,
                    label: `${w.warehouse_name}${w.warehouse_code ? ` (${w.warehouse_code})` : ''}`,
                  }))}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Priority"
                    name="priority"
                    rules={[{ required: true, message: 'Please select priority' }]}
                  >
                    <Select placeholder="Select priority">
                      <Select.Option value="Low">
                        <Tag color="default">Low</Tag>
                      </Select.Option>
                      <Select.Option value="Normal">
                        <Tag color="blue">Normal</Tag>
                      </Select.Option>
                      <Select.Option value="Medium">
                        <Tag color="blue">Medium</Tag>
                      </Select.Option>
                      <Select.Option value="High">
                        <Tag color="orange">High</Tag>
                      </Select.Option>
                      <Select.Option value="Urgent">
                        <Tag color="red">Urgent</Tag>
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Planned Start Date"
                    name="plannedStartDate"
                    rules={[{ required: true, message: 'Please select start date' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Planned End Date"
                    name="plannedEndDate"
                    rules={[{ required: true, message: 'Please select end date' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Work Order Notes"
                name="notes"
                tooltip="Add any special instructions for production team"
              >
                <TextArea rows={3} placeholder="Production notes and instructions..." />
              </Form.Item>
            </Panel>

            <Panel header="Execution classification" key="execution">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Scope"
                    name="executionScope"
                    rules={[{ required: true, message: 'Select scope' }]}
                    extra="Fabrication generates unit-wise operations and allows partial delivery. Other scopes use BOQ-line total quantity with no partial completion or delivery."
                  >
                    <Select placeholder="Select scope" options={WORK_ORDER_EXECUTION_SCOPES.map((v) => ({ value: v, label: v }))} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Deviation"
                    name="deviationType"
                    rules={[{ required: true, message: 'Select deviation' }]}
                  >
                    <Select
                      placeholder="Select deviation"
                      options={WORK_ORDER_DEVIATION_TYPES.map((v) => ({ value: v, label: v }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>

            <Panel header="Client requirements" key="client">
              <Form.Item label="Scope of work (client)" name="clientScopeOfWork">
                <TextArea rows={3} placeholder="Scope of work as required by the client" />
              </Form.Item>
              <Form.Item label="Quality requirements" name="clientQualityRequirements">
                <TextArea rows={3} placeholder="Client / contractual quality requirements" />
              </Form.Item>
            </Panel>

            <Panel header="Responsibility assignment" key="responsibility">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Production supervisor"
                    name="productionSupervisorUserId"
                    rules={[{ required: true, message: 'Select production supervisor' }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select user"
                      loading={usersLoading}
                      options={userOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Quality engineer"
                    name="qualityEngineerUserId"
                    rules={[{ required: true, message: 'Select quality engineer' }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select user"
                      loading={usersLoading}
                      options={userOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>

            <Panel header="Material utilization" key="material">
              <Text type="secondary">
                Add rows for drawing line, specification, and verification. For multiple work orders (one per item),
                each item has its own table. After you pick an item, stock availability is shown (uses the target
                warehouse when selected; otherwise totals across all warehouses).
              </Text>
              {selectedItems.length === 0 ? (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary">Select customer order lines first.</Text>
                </div>
              ) : (
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.grouping !== cur.grouping}>
                  {() =>
                    form.getFieldValue('grouping') === 'one' ? (
                      <Form.List name="sharedMaterials">
                        {(fields, { add, remove }) => (
                          <Space direction="vertical" style={{ width: '100%', marginTop: 12 }} size="middle">
                            {fields.map(({ key, name, ...restField }) => (
                              <Card key={key} size="small" type="inner">
                                <Row gutter={8}>
                                  <Col span={4}>
                                    <Form.Item {...restField} label="Drawing line no." name={[name, 'drawingLineNo']}>
                                      <Input placeholder="Line no." />
                                    </Form.Item>
                                  </Col>
                                  <Col span={5}>
                                    <MatSpecItemSelect
                                      restField={restField}
                                      name={name}
                                      prefixPath={['sharedMaterials']}
                                      inventoryItems={inventoryItems}
                                      loading={inventoryItemsLoading}
                                      stockByItemId={materialStockByItemId}
                                      stockLoading={stockLoading}
                                      stockScopeLabel={materialStockScopeLabel}
                                    />
                                  </Col>
                                  <Col span={4}>
                                    <Form.Item {...restField} label="Material grade" name={[name, 'materialGrade']}>
                                      <Input placeholder="Grade" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={3}>
                                    <Form.Item {...restField} label="Head no." name={[name, 'headNo']}>
                                      <Input placeholder="Head no." />
                                    </Form.Item>
                                  </Col>
                                  <Col span={5}>
                                    <Form.Item
                                      {...restField}
                                      label="Verification"
                                      name={[name, 'verification']}
                                      rules={[{ required: false }]}
                                    >
                                      <Radio.Group>
                                        <Radio value="verified">Verified</Radio>
                                        <Radio value="not_verified">Not verified</Radio>
                                      </Radio.Group>
                                    </Form.Item>
                                  </Col>
                                  <Col span={24}>
                                    <Form.Item {...restField} label="Remarks" name={[name, 'remarks']}>
                                      <Input placeholder="Remarks" />
                                    </Form.Item>
                                  </Col>
                                  <Col>
                                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', marginTop: 28 }} />
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                              Add material utilization row
                            </Button>
                          </Space>
                        )}
                      </Form.List>
                    ) : (
                      <Space direction="vertical" style={{ width: '100%', marginTop: 12 }} size="large">
                        {selectedItems.map((item) => (
                          <div key={item.id}>
                            <Text strong>{item.itemName}</Text>
                            <Form.List name={['lineMaterials', item.id]}>
                              {(fields, { add, remove }) => (
                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                  {fields.map(({ key, name, ...restField }) => (
                                    <Card key={key} size="small" type="inner">
                                      <Row gutter={8}>
                                        <Col span={4}>
                                          <Form.Item {...restField} label="Drawing line no." name={[name, 'drawingLineNo']}>
                                            <Input placeholder="Line no." />
                                          </Form.Item>
                                        </Col>
                                        <Col span={5}>
                                          <MatSpecItemSelect
                                            restField={restField}
                                            name={name}
                                            prefixPath={['lineMaterials', item.id]}
                                            inventoryItems={inventoryItems}
                                            loading={inventoryItemsLoading}
                                            stockByItemId={materialStockByItemId}
                                            stockLoading={stockLoading}
                                            stockScopeLabel={materialStockScopeLabel}
                                          />
                                        </Col>
                                        <Col span={4}>
                                          <Form.Item {...restField} label="Material grade" name={[name, 'materialGrade']}>
                                            <Input placeholder="Grade" />
                                          </Form.Item>
                                        </Col>
                                        <Col span={3}>
                                          <Form.Item {...restField} label="Head no." name={[name, 'headNo']}>
                                            <Input placeholder="Head no." />
                                          </Form.Item>
                                        </Col>
                                        <Col span={5}>
                                          <Form.Item {...restField} label="Verification" name={[name, 'verification']}>
                                            <Radio.Group>
                                              <Radio value="verified">Verified</Radio>
                                              <Radio value="not_verified">Not verified</Radio>
                                            </Radio.Group>
                                          </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                          <Form.Item {...restField} label="Remarks" name={[name, 'remarks']}>
                                            <Input placeholder="Remarks" />
                                          </Form.Item>
                                        </Col>
                                        <Col>
                                          <MinusCircleOutlined
                                            onClick={() => remove(name)}
                                            style={{ color: '#ff4d4f', marginTop: 28 }}
                                          />
                                        </Col>
                                      </Row>
                                    </Card>
                                  ))}
                                  <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                                    Add row for this item
                                  </Button>
                                </Space>
                              )}
                            </Form.List>
                          </div>
                        ))}
                      </Space>
                    )
                  }
                </Form.Item>
              )}
            </Panel>
          </Collapse>
        </Form>

        <Alert
          message="Next Steps After Creation"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>
                {isPr
                  ? 'Work order will be created and linked to this production request'
                  : 'Work order will be created and linked to this customer order'}
              </li>
              <li>Job Inspection may be created automatically depending on configuration</li>
              <li>Execution and material data can be edited until the work order is Closed or Cancelled</li>
            </ul>
          }
          type="success"
          showIcon
        />
      {isPage && (
        <Row justify="end" style={{ marginTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={submitting}>
              Create Work Order
            </Button>
          </Space>
        </Row>
      )}
    </Space>
  );

  if (isPage) {
    return (
      <div style={{ width: '100%' }}>
        {body}
      </div>
    );
  }

  return (
    <Modal
      title={titleBlock}
      open={isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      width={960}
      okText="Create Work Order"
      cancelText="Cancel"
      confirmLoading={submitting}
    >
      {body}
    </Modal>
  );
};

export default SalesOrderToWorkOrderDialog;
